import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Skeleton from '@mui/material/Skeleton'
import palette from '../../../theme/palette'
import { count, inr, inr2, pct2, share, signedInr } from './freightPnlUtils'

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const cellSx = { '& td, & th': { ...num, py: 0.75, px: 1, fontSize: 13, borderColor: palette.grey[200] } }
const headSx = { bgcolor: palette.grey[200], fontWeight: 700, color: 'text.secondary', fontSize: 12 }

function Panel({ title, sub, children, loading }) {
  return (
    <Paper variant='outlined' sx={{ p: 2, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 1, mb: 1.25 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>{title}</Typography>
        {sub && <Typography variant='caption' sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>{sub}</Typography>}
      </Box>
      {loading ? [...Array(5)].map((_, i) => <Skeleton key={i} height={26} />) : children}
    </Paper>
  )
}

/** A compact table that scrolls inside its card when the screen is narrower than its columns. */
function ScrollTable({ children }) {
  return <Box sx={{ overflowX: 'auto' }}><Table size='small' sx={cellSx}>{children}</Table></Box>
}

/** One line of the shipment funnel: label (with its share as a grey note under it) and the count on the right. */
function BaseRow({ label, value, note, level = 0, highlight }) {
  const strong = level === 0
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
      py: 0.75, pl: level ? 1.5 : 1, pr: 1, ml: level ? 1 : 0,
      borderLeft: level ? `2px solid ${palette.grey[300]}` : 'none',
      bgcolor: highlight ? palette.success.lighter : 'transparent', borderRadius: highlight ? 1 : 0,
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: strong ? 700 : 400, color: strong ? 'text.primary' : 'text.secondary', lineHeight: 1.35 }}>{label}</Typography>
        {note && <Typography variant='caption' sx={{ color: 'text.disabled', display: 'block', lineHeight: 1.3 }}>{note}</Typography>}
      </Box>
      <Typography variant='body2' sx={{ ...num, fontWeight: strong ? 700 : 500, color: highlight ? palette.success.dark : 'text.primary' }}>{count(value)}</Typography>
    </Box>
  )
}

const shareNote = (v, of, ofLabel) => (share(v, of) === null ? null : `${pct2(share(v, of))} of ${ofLabel}`)

/**
 * The mail sections that are not tables of merchants or couriers: the shipment base (daily section 2), loss-making
 * AWBs by driver (daily section 3) and the courier cost heads (weekly section 2, against the previous period when it
 * is fully computed). Every number comes from the summary response, built from the same rollup rows as the mails.
 */
export default function BreakdownPanels({ data, segment, loading, withChange }) {
  const s = data?.summary || {}
  const p = data?.previous || {}
  const drivers = data?.drivers || []
  // The backend orders heads by rupee movement (the weekly mail's order); without a full previous period, by amount.
  const heads = withChange ? data?.costHeads || [] : [...(data?.costHeads || [])].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))

  return (
    <Box sx={{
      display: 'grid', gap: 1.5, alignItems: 'start',
      // Phones: one column. Laptops: shipment base on the left, drivers and cost heads stacked on the right.
      // Wide screens: all three in a row.
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(280px, 0.8fr) minmax(0, 1.2fr)', xl: 'minmax(280px, 0.85fr) minmax(360px, 1.15fr) minmax(360px, 1fr)' },
      gridTemplateAreas: { xs: '"base" "drivers" "heads"', md: '"base drivers" "base heads"', xl: '"base drivers heads"' },
    }}>
      <Box sx={{ gridArea: 'base', minWidth: 0 }}>
      <Panel title='Shipment base' sub={segment} loading={loading}>
        <BaseRow label='Booked' value={s.bookedShipments} />
        <BaseRow label='Not in Prozo freight scope' value={s.outOfScopeShipments} note={shareNote(s.outOfScopeShipments, s.bookedShipments, 'booked')} level={1} />
        <BaseRow label='In Prozo freight scope' value={s.totalShipments} />
        <BaseRow label='Merchant rate card missing' value={s.merchantRateMissing} note={shareNote(s.merchantRateMissing, s.totalShipments, 'in scope')} level={1} />
        <BaseRow label='Courier buy rate missing' value={s.courierRateMissing} note={shareNote(s.courierRateMissing, s.totalShipments, 'in scope')} level={1} />
        <BaseRow label='Both rates missing' value={s.bothRatesMissing} note={shareNote(s.bothRatesMissing, s.totalShipments, 'in scope')} level={1} />
        <Box sx={{ mt: 0.75 }}>
          <BaseRow label='Priced, used for profit' value={s.pricedShipments} note={shareNote(s.pricedShipments, s.totalShipments, 'in scope')} highlight />
        </Box>
      </Panel>
      </Box>

      <Box sx={{ gridArea: 'drivers', minWidth: 0 }}>
      <Panel title='Loss-making AWBs by driver' sub={s.lossShipments ? `${count(s.lossShipments)} AWBs · ${inr(s.lossAmount)}` : null} loading={loading}>
        {drivers.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>No priced AWB was sold below its courier rate.</Typography>
        ) : (
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Driver</TableCell>
                <TableCell sx={headSx} align='right'>AWBs</TableCell>
                <TableCell sx={headSx} align='right'>Share</TableCell>
                <TableCell sx={headSx} align='right'>Gross loss</TableCell>
                <TableCell sx={headSx} align='right'>Avg / AWB</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map(d => (
                <TableRow key={d.driver}>
                  <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 140 }}>{d.label}</TableCell>
                  <TableCell align='right'>{count(d.lossShipments)}</TableCell>
                  <TableCell align='right'>{pct2(share(d.lossShipments, s.lossShipments))}</TableCell>
                  <TableCell align='right' sx={{ color: palette.error.dark }}>{inr(d.lossAmount)}</TableCell>
                  <TableCell align='right'>{inr2(d.lossPerShipment)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderBottom: 0, bgcolor: palette.grey[100] } }}>
                <TableCell>Total</TableCell>
                <TableCell align='right'>{count(s.lossShipments)}</TableCell>
                <TableCell align='right'>{pct2(share(s.lossShipments, s.pricedShipments))}<Typography component='span' variant='caption' sx={{ color: 'text.disabled', ml: 0.5 }}>of priced</Typography></TableCell>
                <TableCell align='right' sx={{ color: palette.error.dark }}>{inr(s.lossAmount)}</TableCell>
                <TableCell align='right'>{inr2(s.lossPerShipment)}</TableCell>
              </TableRow>
            </TableBody>
          </ScrollTable>
        )}
      </Panel>
      </Box>

      <Box sx={{ gridArea: 'heads', minWidth: 0 }}>
      <Panel title='Courier cost heads' sub={withChange ? 'vs previous period' : null} loading={loading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Cost head</TableCell>
              <TableCell sx={headSx} align='right'>{withChange ? 'This period' : 'Amount'}</TableCell>
              {withChange && <TableCell sx={headSx} align='right'>Change</TableCell>}
              {withChange && <TableCell sx={headSx} align='right'>vs revenue</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {heads.map(h => (
              <TableRow key={h.name}>
                <TableCell sx={{ whiteSpace: 'normal !important' }}>{h.name}</TableCell>
                <TableCell align='right' sx={{ color: h.amount < 0 ? palette.error.dark : undefined }}>{inr(h.amount)}</TableCell>
                {withChange && <TableCell align='right'>{signedInr(h.amount - h.previousAmount)}</TableCell>}
                {withChange && (
                  <TableCell align='right' sx={{ fontWeight: 600, color: h.vsRevenue === 'Faster' ? palette.error.dark : h.vsRevenue === 'Slower' ? palette.success.dark : undefined }}>
                    {h.vsRevenue}
                  </TableCell>
                )}
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { fontWeight: 700, borderBottom: 0, bgcolor: palette.grey[100] } }}>
              <TableCell>Total cost</TableCell>
              <TableCell align='right'>{inr(s.totalCost)}</TableCell>
              {withChange && <TableCell align='right'>{signedInr((s.totalCost || 0) - (p.totalCost || 0))}</TableCell>}
              {withChange && <TableCell />}
            </TableRow>
          </TableBody>
        </ScrollTable>
        <Typography variant='caption' sx={{ color: 'text.disabled', display: 'block', mt: 1, lineHeight: 1.4 }}>
          Priced AWBs, from each courier rate card. RTO is the separately priced return leg; Others is the rest of the buy price, so the heads add up to total cost.
        </Typography>
      </Panel>
      </Box>
    </Box>
  )
}
